import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import './PasswordResetRequired.css';

const PasswordResetRequired: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 10) {
      setError('Password must be at least 10 characters long');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Send HTTP-only cookies for authentication
        body: JSON.stringify({
          newPassword,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        // Logout and redirect to login after successful password reset
        setTimeout(() => {
          logout();
          navigate('/login');
        }, 2000);
      } else {
        setError(data.error || 'Failed to reset password');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (success) {
    return (
      <div className="password-reset-required">
        <div className="password-reset-card">
          <div className="success-state">
            <div className="success-icon">✅</div>
            <h2>Password Reset Successful!</h2>
            <p>
              Your password has been updated successfully. You will be
              redirected to the login page.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="password-reset-required">
      <div className="password-reset-card">
        <div className="password-reset-header">
          <div className="warning-icon">⚠️</div>
          <h2>Password Reset Required</h2>
          <p>
            You are using a temporary password. Please set a new password to
            continue.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="password-reset-form">
          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="Enter your new password"
              required
              minLength={8}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Confirm your new password"
              required
              minLength={8}
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="form-actions-reset">
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary"
              style={{ width: '100%', marginTop: '10px' }}
            >
              {isSubmitting ? 'Updating Password...' : 'Update Password'}
            </button>

            <button
              type="button"
              onClick={handleLogout}
              className="btn-secondary"
            >
              Logout
            </button>
          </div>
        </form>

        <div className="password-reset-info">
          <p>
            <strong>Password Requirements:</strong>
          </p>
          <ul>
            <li>10 character minimum</li>
            <li>Must contain uppercase (A-Z)</li>
            <li>Must contain lowercase (a-z)</li>
            <li>Must contain number (0-9)</li>
            <li>Must contain special character</li>
            <li>Cannot have more than 3 repeating characters in a row</li>
            <li>Must match confirmation password</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetRequired;
